import { useState, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useApiUrl } from '@refinedev/core';
import { resolveFileUrl } from '@/lib/file-url';
import { getActiveTenant } from '@/lib/active-tenant';

interface FileRecord {
  id: number;
  url: string;
}

interface FileImageUploadProps {
  value: FileRecord | null;
  onChange: (file: FileRecord | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  accept?: string;
  maxSizeMB?: number;
  aspectRatio?: string;
}

interface UploadState {
  isUploading: boolean;
  isDeleting: boolean;
  progress: number;
  error: string | null;
}

const initialState: UploadState = {
  isUploading: false,
  isDeleting: false,
  progress: 0,
  error: null,
};

/**
 * Загрузка изображений с созданием записи в таблице File.
 * Возвращает { id, url } вместо просто URL.
 * Использует endpoint POST /upload/file.
 */
export function FileImageUpload({
  value,
  onChange,
  placeholder = 'Перетащите изображение или нажмите для выбора',
  className,
  disabled = false,
  accept = 'image/*',
  maxSizeMB = 10,
  aspectRatio,
}: FileImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>(initialState);
  const apiUrl = useApiUrl();

  const getAuthToken = useCallback(() => {
    return localStorage.getItem('accessToken');
  }, []);

  const uploadFile = useCallback(
    async (file: File): Promise<FileRecord> => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/upload/file`);

        const token = getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // X-Tenant-Id: XHR минует global fetch interceptor — добавляем вручную,
        // иначе на узб (club) upload падал в default-тенант.
        const tenant = getActiveTenant();
        if (tenant) {
          xhr.setRequestHeader('X-Tenant-Id', tenant);
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadState((prev) => ({ ...prev, progress: percentComplete }));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success && response.data?.id && response.data?.url) {
                resolve({ id: response.data.id, url: response.data.url });
              } else {
                reject(new Error(response.errors?.[0] ?? 'Ошибка загрузки'));
              }
            } catch {
              reject(new Error('Ошибка обработки ответа сервера'));
            }
          } else {
            reject(new Error(`Ошибка загрузки: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Ошибка сети'));
        };

        xhr.send(formData);
      });
    },
    [apiUrl, getAuthToken],
  );

  const deleteFile = useCallback(
    async (fileId: number): Promise<void> => {
      const token = getAuthToken();
      const response = await fetch(`${apiUrl}/files/admin/${fileId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.errors?.[0] ?? 'Ошибка удаления');
      }
    },
    [apiUrl, getAuthToken],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        setUploadState((prev) => ({
          ...prev,
          error: 'Пожалуйста, выберите изображение',
        }));
        return;
      }

      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setUploadState((prev) => ({
          ...prev,
          error: `Размер файла не должен превышать ${maxSizeMB} МБ`,
        }));
        return;
      }

      setUploadState({ isUploading: true, isDeleting: false, progress: 0, error: null });

      try {
        const record = await uploadFile(file);
        setUploadState({ isUploading: false, isDeleting: false, progress: 100, error: null });
        onChange(record);
      } catch (err) {
        setUploadState({
          isUploading: false,
          isDeleting: false,
          progress: 0,
          error: err instanceof Error ? err.message : 'Ошибка загрузки',
        });
      }
    },
    [onChange, uploadFile, maxSizeMB],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {setIsDragging(true);}
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {setIsDragging(true);}
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) {return;}
      const files = e.dataTransfer.files;
      if (files.length > 0) {handleFileSelect(files[0]);}
    },
    [disabled, handleFileSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {handleFileSelect(files[0]);}
      e.target.value = '';
    },
    [handleFileSelect],
  );

  const handleRemove = useCallback(async () => {
    if (!value) {return;}
    setUploadState((prev) => ({ ...prev, isDeleting: true, error: null }));

    try {
      await deleteFile(value.id);
    } catch {
      // Даже если удаление на сервере не удалось, очищаем значение
    }
    onChange(null);
    setUploadState(initialState);
  }, [onChange, deleteFile, value]);

  const handleClick = useCallback(() => {
    if (!disabled && !uploadState.isUploading && !uploadState.isDeleting) {
      inputRef.current?.click();
    }
  }, [disabled, uploadState.isUploading, uploadState.isDeleting]);

  const isProcessing = uploadState.isUploading || uploadState.isDeleting;

  return (
    <div className={cn('space-y-2', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {value ? (
        <div
          className="relative group rounded-md overflow-hidden border bg-muted/30"
          style={aspectRatio ? { aspectRatio } : undefined}
        >
          <img
            src={resolveFileUrl(value.url)}
            alt={placeholder}
            loading="lazy"
            className={cn('w-full object-cover', aspectRatio ? 'h-full' : 'h-48')}
          />
          {!disabled && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {uploadState.isDeleting ? (
                <div className="flex items-center gap-2 text-white">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Удаление...</span>
                </div>
              ) : (
                <>
                  <Button type="button" variant="secondary" size="sm" onClick={handleClick} disabled={isProcessing}>
                    <Upload className="h-4 w-4 mr-1" />
                    Заменить
                  </Button>
                  <Button type="button" variant="destructive" size="sm" onClick={handleRemove} disabled={isProcessing}>
                    <X className="h-4 w-4 mr-1" />
                    Удалить
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            'relative border-2 border-dashed rounded-md text-center cursor-pointer transition-colors',
            aspectRatio ? 'flex items-center justify-center' : 'p-8',
            isDragging && 'border-primary bg-primary/5',
            !isDragging && 'border-muted-foreground/25 hover:border-primary/50',
            disabled && 'opacity-50 cursor-not-allowed',
            uploadState.error && 'border-destructive',
          )}
        >
          <div style={aspectRatio ? { aspectRatio } : undefined} className={cn(aspectRatio && 'flex items-center justify-center w-full')}>
            {uploadState.isUploading ? (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" />
                <div className="w-full max-w-xs">
                  <Progress value={uploadState.progress} className="h-2" />
                </div>
                <p className="text-sm text-muted-foreground">Загрузка... {uploadState.progress}%</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <ImageIcon className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{placeholder}</p>
                <p className="text-xs text-muted-foreground/70">Максимальный размер: {maxSizeMB} МБ</p>
              </div>
            )}
          </div>
        </div>
      )}

      {uploadState.error && <p className="text-sm text-destructive">{uploadState.error}</p>}
    </div>
  );
}
